/**
 * Mise en page du ticket sur l'imprimante Sunmi intégrée.
 *
 * Tout passe en texte aligné par des espaces : le mode colonnes du firmware ajoute un
 * interligne que le réglage d'espacement n'enlève pas (constaté sur D3 80 mm le 2026-10-31).
 * Un texte trop long passe à la ligne : un ticket ne perd jamais une information.
 */

/** Taille native : 24 points = 48 colonnes en 80 mm, 32 en 58 mm. En dessous, le ticket devient illisible. */
export const AIDL_FONT_SIZE = 24;

/** ESC 3 n : interligne en points, valeur choisie sur papier (D3 80 mm, 2026-10-31). */
export const AIDL_LINE_SPACING = [0x1b, 0x33, 8];

/** ESC 2 : interligne par défaut, pour que l'avance avant la coupe garde sa longueur. */
export const AIDL_DEFAULT_SPACING = [0x1b, 0x32];

/** Avance avant la coupe : la distance entre la tête et la lame, plus une ou deux lignes de marge. */
export const AIDL_FEED_BEFORE_CUT = 8;

/**
 * Mise en page réservée au D3 à imprimante intégrée (modèle « D3_80MM »). Le D3 Mini et les V2
 * gardent leur sortie ; le D3 Pro n'a pas d'imprimante intégrée. Modèle inconnu : sortie d'origine.
 */
export function usesD3ReceiptLayout(model: string = readDeviceModel()): boolean {
  const normalized = model.toLowerCase().replace(/[\s_-]/g, '');
  return normalized.startsWith('d3') && !normalized.includes('mini') && !normalized.includes('pro');
}

function readDeviceModel(): string {
  return (window as Window & { __VISIONPOS_DEVICE_MODEL?: string }).__VISIONPOS_DEVICE_MODEL ?? '';
}

export type LineAlign = 0 | 1 | 2;

const headDots = (paperMm: number): number => (paperMm === 58 ? 384 : 576);

/** Colonnes disponibles : un caractère occupe une demi-taille de police en largeur. */
export function columnsAt(fontSize: number, paperMm: number): number {
  return Math.floor(headDots(paperMm) / (fontSize / 2));
}

/** Coupe sur les espaces ; un mot plus long que la ligne est coupé sans perdre de caractère. */
export function wrapToWidth(text: string, width: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    let rest = word;
    while (rest.length > width) {
      if (line) lines.push(line);
      line = '';
      lines.push(rest.slice(0, width));
      rest = rest.slice(width);
    }
    if (!line) line = rest;
    else if (line.length + 1 + rest.length <= width) line += ' ' + rest;
    else {
      lines.push(line);
      line = rest;
    }
  }
  if (line || lines.length === 0) lines.push(line);
  return lines;
}

/** Ligne alignée : 0 gauche, 1 centre, 2 droite. */
export function formatLine(text: string, align: LineAlign, columns: number): string[] {
  return wrapToWidth(text, columns).map((line) => {
    const free = columns - line.length;
    const pad = align === 1 ? Math.floor(free / 2) : align === 2 ? free : 0;
    return ' '.repeat(Math.max(0, pad)) + line;
  });
}

/** Libellé à gauche, montant à droite sur la première ligne ; la suite du libellé dessous. */
export function formatRow(left: string, right: string, columns: number): string[] {
  const parts = wrapToWidth(left, Math.max(1, columns - right.length - 1));
  return parts.map((part, index) =>
    index === 0 ? part + ' '.repeat(Math.max(1, columns - part.length - right.length)) + right : part,
  );
}
